import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Doorly runs two Stripe accounts (operating for showing fees, escrow for
// offer fees). The backend's /pay/create-intent response tells us which
// publishable key to load Stripe.js with — using the wrong account's key
// would make Stripe.js reject the client_secret as "from a different
// account". Cache loadStripe() per key so repeat opens of the modal don't
// re-fetch the Stripe.js bundle.
const stripeCache = new Map();
function getStripePromise(publishableKey) {
  if (!publishableKey || !publishableKey.startsWith('pk_')) return null;
  if (!stripeCache.has(publishableKey)) {
    stripeCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripeCache.get(publishableKey);
}

// ── Checkout form (inside <Elements>) ─────────────────────────────────────────
function CheckoutForm({ amount, description, note, paymentType, showingId, paymentIntentId, publishableKey, extraBody, onSuccess, onCancel }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError]   = useState(null);
  const [ready, setReady]   = useState(false);
  // Stripe's PaymentElement reports completeness via onChange. Without this
  // gate, the Pay button is clickable before the borrower has entered full
  // card details — clicking it then bounces with an inline error, but the
  // PaymentIntent was already created at modal open and is now orphaned in
  // requires_payment_method state. Drives the "Stripe says no payment method
  // selected" support tickets.
  const [paymentComplete, setPaymentComplete] = useState(false);
  // mountFailed flips true when the 20s watchdog fires. Surfaces the
  // "Use our backup checkout page" button to borrowers whose browser
  // can't load the embedded PaymentElement iframe.
  const [mountFailed, setMountFailed]         = useState(false);
  const [fallbackRedirecting, setFallbackRedirecting] = useState(false);

  const token = localStorage.getItem('token') || localStorage.getItem('doorly_token');

  // 20s PaymentElement mount watchdog — Stripe.js can silently fail to
  // mount the iframe on flaky networks, ad-blocker interference, or
  // wrong publishable_key mismatches. Without this, the Pay button
  // sits on "Loading…" forever and the borrower has no signal that
  // anything's wrong (this is the hang point Grace's screenshot is
  // currently catching — "Loading…" + 20s watchdog error). When it
  // fires we ALSO POST to /pay/log-failure so an alert email lands
  // in alerts@ with the orphaned intent id + browser context; without
  // that, the only signal is a "Payment started" intent on Stripe
  // dashboard with no actor.
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => {
      setMountFailed(true);
      setError('Payment form couldn\'t load in your browser. This usually happens with content blockers, ad blockers, or older mobile browsers. You can use our backup checkout page below — it works on any browser.');
      try {
        fetch(`${API}/api/auth/pay/log-failure`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            payment_intent_id: paymentIntentId || null,
            payment_type:      paymentType,
            intent_status:     'mount_timeout',
            error_code:        'PAYMENT_ELEMENT_MOUNT_TIMEOUT_20S',
            error_message:     'Stripe.js PaymentElement onReady never fired within 20 seconds — iframe failed to mount. Common causes: ad blocker, content blocker, 3rd-party cookies disabled, mobile Safari ITP, corporate firewall blocking js.stripe.com, publishable_key/secret_key account mismatch.',
            error_payment_method_type: null,
            browser_user_agent: navigator.userAgent,
            browser_url:        window.location.href,
            // Surface the publishable key prefix (NOT the secret) so
            // we can compare it against the [Create Intent] Render
            // log's sk_/pk_ account_match line for the same intent.
            publishable_key_prefix: publishableKey ? publishableKey.slice(0, 14) : null,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    }, 20000);
    return () => clearTimeout(t);
  }, [ready, paymentIntentId, paymentType, publishableKey, token]);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    if (!paymentComplete) {
      setError('Please complete your payment details above before tapping Pay.');
      return;
    }
    setPaying(true);
    setError(null);

    // Fire-and-forget diagnostic logger. Called whenever the confirm flow
    // doesn't reach 'succeeded' / 'requires_capture' so we can see WHY a
    // PaymentIntent went dead (Apple Pay sheet dismissed, card declined,
    // 3DS abandoned, Cash App redirect lost, etc.). Without this we have
    // no signal beyond "incomplete / payment started" in Stripe Dashboard.
    const logFailure = ({ intent, stripeErr }) => {
      try {
        fetch(`${API}/api/auth/pay/log-failure`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            payment_intent_id: intent?.id || null,
            payment_type:      paymentType,
            intent_status:     intent?.status || null,
            error_code:               stripeErr?.code || intent?.last_payment_error?.code || null,
            error_message:            stripeErr?.message || intent?.last_payment_error?.message || null,
            error_decline_code:       stripeErr?.decline_code || intent?.last_payment_error?.decline_code || null,
            error_payment_method_type: stripeErr?.payment_method?.type
                                        || intent?.last_payment_error?.payment_method?.type
                                        || intent?.payment_method?.type
                                        || null,
            browser_user_agent: navigator.userAgent,
            browser_url:        window.location.href,
          }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    };

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/dashboard?payment_complete=1`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        // Be explicit that the attempt did NOT go through — borrowers
        // were closing the modal after seeing a brief error and assuming
        // their payment had been submitted.
        logFailure({ intent: paymentIntent, stripeErr: confirmError });
        const baseMsg = confirmError.message || 'Payment failed. Please try again.';
        // ── 🚨 THE FALLBACK SUGGESTION CANNOT OFFER A CARD FOR A DEPOSIT ──
        //
        // This told a borrower whose bank transfer failed to "try paying with a
        // card or Link instead" — and offer_fee is the Purchase Initiation
        // Deposit, which is bank transfer only. The suggestion was unreachable
        // advice at best; at worst it sent somebody looking for a card field
        // that is deliberately not there, on the one charge where a card is not
        // permitted. The deposit gets the other true option: a wire.
        const pmType = confirmError?.payment_method?.type;
        const suggestion = paymentType === 'offer_fee'
          ? ' If this keeps happening, ask us for wire instructions instead.'
          : (pmType && pmType !== 'card' && pmType !== 'link'
            ? ' If this keeps happening, try paying with a card or Link instead.'
            : '');
        setError(`${baseMsg}${suggestion} Your payment was NOT submitted — no charge has been made.`);
        setPaying(false);
        return;
      }

      // ACH offer fees authorize as 'processing' (debit settles in 4–5 days),
      // so treat 'processing' as success for offer_fee (optimistic submission —
      // the backend webhook reverses if the debit later returns). 'requires_action'
      // (ACH microdeposit fallback) is NOT success — it routes to the resume flow.
      const okStatuses = paymentType === 'offer_fee'
        ? ['succeeded', 'requires_capture', 'processing']
        : ['succeeded', 'requires_capture'];
      if (paymentIntent && okStatuses.includes(paymentIntent.status)) {
        // 30s AbortController timeout — backend /pay/confirm now caps at
        // 15s on the Stripe retrieve, but we still need a client-side
        // timeout to cover network stalls before the request even
        // reaches the server. Bigger than backend so the server-side
        // timeout's 504 wins the race when it's a Stripe-side problem.
        const confirmController = new AbortController();
        const confirmTimeout = setTimeout(() => confirmController.abort(), 30000);
        let confirmRes;
        try {
          confirmRes = await fetch(`${API}/api/auth/pay/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              payment_intent_id: paymentIntent.id,
              payment_type: paymentType,
              showing_id: showingId || null,
            }),
            signal: confirmController.signal,
          });
        } catch (fetchErr) {
          clearTimeout(confirmTimeout);
          if (fetchErr.name === 'AbortError') {
            setError('Confirming your payment timed out after 30 seconds. Your card was charged — please contact support@godoorly.com with your confirmation number so we can finalize manually. Do NOT retry the payment.');
          } else {
            setError(`Could not reach Doorly to finalize your payment. ${fetchErr.message}. Your card was charged — please contact support if this persists.`);
          }
          setPaying(false);
          return;
        }
        clearTimeout(confirmTimeout);
        const data = await confirmRes.json();
        if (data.success) {
          // Forward the verified payment intent id so the consumer can
          // pass it to downstream record-creation calls (e.g. /api/homes/offer)
          // where the backend re-verifies the intent before persisting.
          onSuccess?.({ ...data, payment_intent_id: data.payment_intent_id || paymentIntent.id });
        } else {
          setError(data.error || 'Payment confirmed but record update failed. Contact support.');
        }
      } else if (paymentIntent?.status === 'processing') {
        onSuccess?.({ processing: true, payment_intent_id: paymentIntent?.id });
      } else {
        // Confirm completed without a stripeError BUT the intent isn't in
        // a success state. Most common cause we've actually seen in prod
        // is requires_payment_method, which happens when the borrower's
        // wallet (Apple Pay sheet / Cash App redirect) was dismissed
        // before completing. Log full diagnostics so we can triage and
        // route the borrower to a more reliable method.
        logFailure({ intent: paymentIntent });
        const status = paymentIntent?.status || 'unknown';
        const lastErr = paymentIntent?.last_payment_error;
        const pmType  = lastErr?.payment_method?.type || paymentIntent?.payment_method?.type;
        let msg;
        if (status === 'requires_payment_method') {
          // 🚨 SAME RULE. A deposit that did not complete is retried as a bank
          // transfer or sent as a wire — never as a card.
          msg = paymentType === 'offer_fee'
            ? 'That bank transfer didn\'t complete. Try connecting your bank again, or ask us '
              + 'for wire instructions.'
            : (pmType && pmType !== 'card' && pmType !== 'link'
              ? `${pmType.replace(/_/g, ' ')} didn't complete. Please try paying with a card or Link.`
              : 'Payment didn\'t complete. Please try a different card, or use Link.');
        } else if (status === 'requires_action') {
          msg = 'Additional verification was required but not completed. Please try again.';
        } else if (lastErr?.message) {
          msg = paymentType === 'offer_fee'
            ? `${lastErr.message} Try connecting your bank again, or ask us for wire instructions.`
            : `${lastErr.message} Please try a different payment method.`;
        } else {
          msg = `Payment did not complete (status: ${status}). Please try again or use a different payment method.`;
        }
        setError(msg);
      }
    } catch (e) {
      logFailure({ stripeErr: { message: e?.message, code: 'CLIENT_EXCEPTION' } });
      setError(e.message || 'An unexpected error occurred.');
    } finally {
      setPaying(false);
    }
  };

  // Fallback to Stripe-hosted Checkout. Triggered by the borrower
  // clicking the "Use our backup checkout page" button after the
  // PaymentElement iframe fails to mount in their browser (or any
  // time they prefer it). Creates a fresh Checkout Session on our
  // backend (which stamps offer/showing context into Stripe metadata),
  // then full-page-redirects to checkout.stripe.com — no iframe, no
  // embedded JS, no 3rd-party cookies required.
  const useStripeCheckout = async () => {
    if (fallbackRedirecting) return;
    setFallbackRedirecting(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/auth/pay/checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          payment_type: paymentType,
          showing_id:   showingId || null,
          listing_id:   extraBody?.listing_id || null,
          address:      extraBody?.address    || null,
          notes:        extraBody?.notes      || null,
        }),
      });
      const data = await r.json();
      if (data.already_paid || data.already_waived) {
        // Mirror the parent's short-circuit handling so the borrower
        // doesn't bounce out to Stripe on a no-op charge.
        onSuccess?.({ ...data, success: true });
        return;
      }
      if (!data.url) {
        setError(data.error || 'Could not start backup checkout. Please refresh and try again.');
        setFallbackRedirecting(false);
        return;
      }
      // Full-page redirect — works on any browser regardless of
      // iframe/3rd-party-cookie/ad-blocker state.
      window.location.href = data.url;
    } catch (e) {
      setError(`Could not start backup checkout: ${e.message}`);
      setFallbackRedirecting(false);
    }
  };

  const disabled = !stripe || !elements || !ready || paying || !paymentComplete;

  return (
    <div>
      {/* Amount */}
      <div style={{ background: '#f0f4ff', border: '1px solid #c5d3f5', borderRadius: '12px', padding: '18px 20px', marginBottom: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', fontWeight: '900', color: '#3a6ad9' }}>{amount}</div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{description}</div>
      </div>

      {note && (
        <div style={{ fontSize: '12px', color: '#6b7280', background: '#f9fafb', padding: '10px 12px', borderRadius: '8px', marginBottom: '16px', lineHeight: '1.6' }}>
          {note}
        </div>
      )}

      {/* ACH debit authorization — offer_fee is collected via us_bank_account,
          so surface an explicit one-time authorization above the Element. */}
      {paymentType === 'offer_fee' && (
        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 14,
          fontSize: 12,
          color: '#6b7280',
          lineHeight: 1.6,
        }}>
          By providing your bank account information and
          confirming this payment, you authorize Go Doorly,
          LLC (NMLS #2764749) to debit your account via ACH
          for the purchase initiation deposit shown above. This is a
          one-time authorization for this transaction only.
        </div>
      )}

      {/* Payment Element */}
      <div style={{ marginBottom: '16px' }}>
        <PaymentElement
          onReady={() => setReady(true)}
          onChange={(e) => setPaymentComplete(!!e.complete)}
          options={{
            layout: 'tabs',
            // The intent's payment_method_types (set in auth.js) decides what
            // shows. offer_fee is us_bank_account (ACH/Financial Connections);
            // showing_fee is card + link. paymentMethodOrder is a no-op for
            // methods not on the intent, so listing all three is safe.
            paymentMethodOrder: paymentType === 'offer_fee'
              ? ['us_bank_account']
              : ['card', 'link'],
          }}
        />
      </div>

      {error && (
        <div style={{ padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>
          {error}
        </div>
      )}

      <button
        onClick={handlePay}
        disabled={disabled}
        style={{
          width: '100%', padding: '14px',
          background: disabled ? '#e5e7eb' : '#fffd5b',
          color: disabled ? '#9ca3af' : '#1a1a1a',
          border: 'none', borderRadius: '12px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontWeight: '900', fontSize: '16px', marginBottom: '8px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}
      >
        {paying ? (
          <>
            <div style={{ width: '14px', height: '14px', border: '2px solid #9ca3af', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
            Processing...
          </>
        ) : !ready ? (
          <>
            <div style={{ width: '14px', height: '14px', border: '2px solid #9ca3af', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
            Loading...
          </>
        ) : !paymentComplete ? (
          paymentType === 'offer_fee' ? 'Verify your bank account above to continue' : 'Enter card details above to continue'
        ) : `Pay ${amount}`}
      </button>

      {/* Stripe Checkout fallback — promoted as the primary action
          when the embedded PaymentElement failed to mount, subtle
          link otherwise. The hosted Checkout path is a full-page
          redirect (no iframe) so it works on browsers where Stripe's
          embedded JS gets blocked or sandboxed.
          NOT shown for offer_fee: that path is ACH-only (us_bank_account)
          and the hosted card-checkout fallback is disabled server-side. */}
      {paymentType !== 'offer_fee' && (mountFailed ? (
        <button
          onClick={useStripeCheckout}
          disabled={fallbackRedirecting}
          style={{
            width: '100%', padding: '14px',
            background: fallbackRedirecting ? '#e5e7eb' : '#3a6ad9',
            color: fallbackRedirecting ? '#9ca3af' : '#fff',
            border: 'none', borderRadius: '12px',
            cursor: fallbackRedirecting ? 'not-allowed' : 'pointer',
            fontWeight: '900', fontSize: '15px', marginBottom: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
        >
          {fallbackRedirecting ? (
            <>
              <div style={{ width: '14px', height: '14px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
              Redirecting…
            </>
          ) : `Continue to backup checkout (${amount})`}
        </button>
      ) : (
        <button onClick={useStripeCheckout} disabled={fallbackRedirecting}
          style={{ width: '100%', padding: '10px', background: 'none', border: 'none',
                   cursor: fallbackRedirecting ? 'not-allowed' : 'pointer',
                   fontSize: '12px', color: '#6b7280', fontWeight: '600',
                   textDecoration: 'underline', marginBottom: '4px' }}>
          {fallbackRedirecting ? 'Redirecting…' : 'Trouble loading the form? Use our backup checkout page'}
        </button>
      ))}

      {onCancel && (
        <button onClick={onCancel}
          style={{ width: '100%', padding: '11px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#9ca3af', fontWeight: '600' }}>
          Cancel
        </button>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Main PaymentModal ─────────────────────────────────────────────────────────
//
// Two modes:
//   (default) Create a brand-new PaymentIntent via POST /pay/create-intent.
//             Used when the borrower starts a fresh offer or showing flow.
//   (resume)  Pass `resumeTransactionId` (a payment_transactions.id) to skip
//             create-intent and instead GET /pay/resume/:id, which returns the
//             live client_secret for an in-flight intent (typically one stuck
//             in requires_action after a 3DS challenge the borrower didn't
//             finish). The original intent metadata (listing_id, address,
//             notes) is forwarded back through onSuccess so the consumer can
//             re-POST to /api/homes/offer.
function PaymentModal({ isOpen, onClose, onSuccess, type, extraBody = {}, resumeTransactionId = null }) {
  const [clientSecret, setClientSecret]       = useState(null);
  const [publishableKey, setPublishableKey]   = useState(null);
  // Surfaced so CheckoutForm's mount watchdog can attribute the
  // log-failure POST to a specific Stripe PaymentIntent (the ones
  // showing as "incomplete / payment started" on the dashboard).
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [amountCents, setAmountCents]         = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [shortCircuit, setShortCircuit]       = useState(null);
  const [resumeMetadata, setResumeMetadata]   = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('doorly_token');

  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  const configs = {
    // ── 🚨 THIS NOTE SAID "Non-refundable" ON THE PAYMENT SCREEN ──────────
    //
    // It read: "Applied as a lender credit at closing. Non-refundable; if this
    // property is disqualified, the credit applies to your next offer." Every
    // clause of that contradicts §12 as Jesse rewrote it. §12 says this money is
    // a DEPOSIT toward the purchase and not a fee, that Doorly holds it in a
    // separate account, that it is CREDITED IN FULL toward the purchase at
    // closing — not applied as a lender credit — and that the borrower "may
    // request a full refund" if the seller declines, if the home does not
    // qualify, or if they decide not to continue before signing a purchase
    // agreement.
    //
    // 🔑 SO THE SCREEN WHERE THE MONEY IS TAKEN WAS TELLING BORROWERS THE
    // OPPOSITE OF THE AGREEMENT, on the one point they would act on. The rename
    // changed the title to "Purchase Initiation Deposit" and left the sentence
    // underneath describing the old non-refundable fee.
    //
    // ⚠️ THE WORDING TRACKS §12 RATHER THAN PARAPHRASING IT. "a separate
    // account", "in full", "a full refund" and "before you sign a purchase
    // agreement" are §12's own words, so the two cannot drift into saying
    // different things about the same dollar.
    offer_fee: {
      title: 'Purchase Initiation Deposit',
      amount: '$500.00', // fallback only — real amount comes from amount_cents
      description: '2% of the home’s list price, paid by bank transfer',
      note: 'Paid by bank transfer — bank account or wire. This is a deposit toward your '
        + 'purchase, not a fee: Doorly holds it in a separate account and credits it in full '
        + 'toward your purchase at closing. Before you sign a purchase agreement with Doorly, '
        + 'you can request a full refund.',
    },
    showing_fee: {
      title: 'Showing Fee',
      amount: '$60.00',
      description: 'Property showing fee',
      note: 'A showing agent will coordinate access directly with you after payment.',
    },
  };
  const config = configs[type] || configs.offer_fee;

  // Fetch PaymentIntent client_secret + per-account publishable_key when
  // the modal opens. Both must come from the same backend call — using a
  // publishable key from the wrong account makes Stripe.js reject the
  // client_secret with "intent does not match this account". In default
  // mode this is /pay/create-intent (creates a new intent); in resume
  // mode it's /pay/resume/:id (returns live state for an existing intent).
  useEffect(() => {
    if (!isOpen || !type) return;
    setClientSecret(null);
    setPublishableKey(null);
    setPaymentIntentId(null);
    setShortCircuit(null);
    setError(null);
    setResumeMetadata(null);
    setLoading(true);

    const resumeMode = !!resumeTransactionId;
    // 30s AbortController — backend now caps Stripe paymentIntents.create
    // at 15s and returns 504, but a slow network before the request lands
    // still needs a client-side cap. Without this the modal sits on the
    // "Setting up payment…" spinner indefinitely (this was one of Grace's
    // hang points). The cleanup function aborts in-flight requests when
    // the modal closes so a slow response doesn't fire setState on an
    // unmounted component.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const request = resumeMode
      ? fetch(`${API}/api/auth/pay/resume/${resumeTransactionId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
      : fetch(`${API}/api/auth/pay/create-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            payment_type: type,
            showing_id: extraBody?.showing_id || null,
            // offer_fee context — backend stamps these into intent metadata
            // so the borrower can resume after abandoning mid-3DS.
            listing_id: extraBody?.listing_id || null,
            address:    extraBody?.address    || null,
            notes:      extraBody?.notes      || null,
            // Drives the dynamic offer fee = 2% of list price (min $500).
            list_price: extraBody?.list_price || null,
          }),
          signal: controller.signal,
        });

    request
      .then(r => r.json())
      .then(data => {
        if (data.client_secret) {
          setClientSecret(data.client_secret);
          setPublishableKey(data.publishable_key || null);
          setPaymentIntentId(data.payment_intent_id || null);
          setAmountCents(typeof data.amount_cents === 'number' ? data.amount_cents : null);
          if (resumeMode && data.metadata) setResumeMetadata(data.metadata);
        } else if (data.already_paid || data.complimentary || data.success) {
          setShortCircuit(data);
        } else {
          setError(data.error || 'Could not initialize payment. Please try again.');
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') {
          setError('Setting up payment timed out after 30 seconds. Please check your connection and try again. If this keeps happening, contact support@godoorly.com.');
        } else {
          setError('Network error. Please check your connection.');
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [isOpen, type, resumeTransactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle short-circuit (complimentary showing, fee already paid)
  useEffect(() => {
    if (shortCircuit) {
      onSuccess?.(shortCircuit);
      onClose?.();
    }
  }, [shortCircuit]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#3a6ad9',
      colorBackground: '#ffffff',
      colorText: '#111827',
      colorDanger: '#ff082f',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      spacingUnit: '4px',
      borderRadius: '10px',
      fontSizeBase: '14px',
    },
    rules: {
      '.Input': { border: '1px solid #d1d5db', boxShadow: 'none' },
      '.Input:focus': { border: '2px solid #3a6ad9', boxShadow: 'none', outline: 'none' },
      '.Tab': { border: '1px solid #e5e7eb' },
      '.Tab--selected': { border: '2px solid #3a6ad9', backgroundColor: '#f0f4ff' },
      '.Label': { fontSize: '12px', fontWeight: '600', color: '#374151' },
    },
  };

  return (
    <div className="safe-modal-overlay" style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '20px', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
    }}>
      <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', margin: 'auto 0', flexShrink: 0 }}>

        {/* Header */}
        <div style={{ background: '#3a6ad9', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '900', color: '#fffd5b', letterSpacing: '-0.3px' }}>Doorly</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>{config.title}</div>
          </div>
          <button onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
              <div style={{ width: '28px', height: '28px', border: '3px solid #e5e7eb', borderTopColor: '#3a6ad9', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '14px' }}>Setting up payment...</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div style={{ padding: '16px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#ff082f', fontWeight: '600', marginBottom: '12px' }}>{error}</div>
              <button onClick={onClose}
                style={{ padding: '10px 24px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                Close
              </button>
            </div>
          )}

          {/* No Stripe key */}
          {!stripePromise && !loading && !error && (
            <div style={{ padding: '16px', background: '#fffbef', border: '1px solid #ffe4a0', borderRadius: '12px', textAlign: 'center', fontSize: '14px', color: '#7a5200' }}>
              Payment processing is being set up. Please contact{' '}
              <a href="mailto:support@godoorly.com" style={{ color: '#3a6ad9' }}>support@godoorly.com</a>
            </div>
          )}

          {/* Payment Element */}
          {clientSecret && stripePromise && !loading && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
              <CheckoutForm
                amount={amountCents != null ? `$${(amountCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : config.amount}
                description={config.description}
                note={resumeTransactionId ? 'Complete your bank verification to finish this payment.' : config.note}
                paymentType={type}
                showingId={extraBody?.showing_id}
                paymentIntentId={paymentIntentId}
                publishableKey={publishableKey}
                extraBody={extraBody}
                onSuccess={(data) => {
                  // In resume mode, forward the original intent metadata so
                  // the consumer can complete any post-payment side-effects
                  // (e.g. /api/homes/offer) using listing_id/address/notes
                  // from the intent rather than fresh client state.
                  onSuccess?.({ ...data, ...(resumeMetadata ? { metadata: resumeMetadata } : {}) });
                  onClose?.();
                }}
                onCancel={onClose}
              />
            </Elements>
          )}
        </div>

        {/* Footer */}
        <div style={{ background: '#f0f4ff', borderTop: '1px solid #c5d3f5', padding: '12px 28px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3a6ad9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span style={{ fontSize: '11px', color: '#3a6ad9' }}>Secured by Stripe. Your payment info is never stored on Doorly servers.</span>
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;
